import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Tracks `prefers-reduced-motion`, live. Users flip this mid-session more often
 * than you would think — usually right after something moved too much.
 */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, getReducedMotion, () => false);
}

function query(): MediaQueryList | null {
  if (typeof window === 'undefined' || !window.matchMedia) return null;
  return window.matchMedia('(prefers-reduced-motion: reduce)');
}

function subscribeReducedMotion(onChange: () => void): () => void {
  const mq = query();
  if (!mq) return () => {};
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function getReducedMotion(): boolean {
  return query()?.matches ?? false;
}

/** Live media-query match, for the few cases CSS alone cannot express. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mq = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query) : null;
      if (!mq) return () => {};
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    },
    [query],
  );

  const snapshot = useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(subscribe, snapshot, () => false);
}

/**
 * A requestAnimationFrame loop that is only mounted while `active`.
 *
 * The callback is kept in a ref so a re-render never restarts the loop, and the
 * elapsed time is measured from the first frame rather than from mount, so a
 * slow first paint does not eat into Jeff's walk.
 */
export function useAnimationFrame(active: boolean, onFrame: (elapsedMs: number) => void): void {
  const callback = useRef(onFrame);

  // Refreshed after every render rather than during it. Frames are requested
  // from an effect and fire after paint, so the loop always sees the callback
  // from the most recent commit.
  useEffect(() => {
    callback.current = onFrame;
  });

  useEffect(() => {
    if (!active) return;
    let raf = 0;
    let start: number | null = null;

    const tick = (now: number) => {
      start ??= now;
      callback.current(now - start);
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);
}

/** Sets document.title, restoring the previous value on unmount. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
